export interface PendingUpload {
  id: string
  file: File
  previewUrl: string | null
  status: 'pending' | 'uploading' | 'done' | 'error'
  attachmentId?: number
  secureUrl?: string
  error?: string
}

export interface AttachmentInfo {
  id: number
  url: string
  name: string
  size: number
  type: string
}

export interface UploadAttachmentResponse {
  attachmentId: number
  secureUrl: string
  fileSize?: number
  processedSize?: number
}

export type UploadSuccess = {
  success: true
  file: File
  attachmentId: number
  secureUrl: string
}

export type UploadFailure = {
  success: false
  file: File
  error: string
}

export type UploadResult = UploadSuccess | UploadFailure

const MAX_FILES = 3
const MAX_FILE_SIZE = 10 * 1024 * 1024
const PASTE_MIME_WHITELIST = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const DROP_MIME_WHITELIST = [
  ...PASTE_MIME_WHITELIST,
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

export class FilePasteHandler {
  private uploadQueue: PendingUpload[] = []
  private attached = false

  constructor(
    private widgetContainer: HTMLElement,
    private textArea: HTMLTextAreaElement,
    private onFileQueued: (file: PendingUpload) => void,
    private onMaxFilesReached: () => void,
    private onValidationError: (file: File, reason: string) => void,
    private uploadFn: (file: File) => Promise<UploadAttachmentResponse>
  ) {}

  attach(): void {
    if (this.attached) return
    this.textArea.addEventListener('paste', this.handlePaste)
    this.widgetContainer.addEventListener('dragenter', this.handleDragEnter)
    this.widgetContainer.addEventListener('dragover', this.handleDragOver)
    this.widgetContainer.addEventListener('dragleave', this.handleDragLeave)
    this.widgetContainer.addEventListener('drop', this.handleDrop)
    this.attached = true
  }

  destroy(): void {
    if (this.attached) {
      this.textArea.removeEventListener('paste', this.handlePaste)
      this.widgetContainer.removeEventListener('dragenter', this.handleDragEnter)
      this.widgetContainer.removeEventListener('dragover', this.handleDragOver)
      this.widgetContainer.removeEventListener('dragleave', this.handleDragLeave)
      this.widgetContainer.removeEventListener('drop', this.handleDrop)
      this.attached = false
    }

    this.widgetContainer.classList.remove('drop-zone-active')
    this.clearQueue()
  }

  getQueue(): readonly PendingUpload[] {
    return this.uploadQueue
  }

  removeFromQueue(id: string): void {
    const idx = this.uploadQueue.findIndex(item => item.id === id)
    if (idx === -1) return

    const [removed] = this.uploadQueue.splice(idx, 1)
    if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl)
  }

  async uploadAll(): Promise<UploadResult[]> {
    const toUpload = this.uploadQueue.filter(item => item.status !== 'done')
    const results = await Promise.all(toUpload.map(item => this.uploadSingleSafe(item)))

    results.forEach((res, idx) => {
      const item = toUpload[idx]
      if (res.success) {
        item.status = 'done'
        item.attachmentId = res.attachmentId
        item.secureUrl = res.secureUrl
        item.error = undefined
      } else {
        item.status = 'error'
        item.error = res.error
      }
    })

    const doneAlready = this.uploadQueue
      .filter(item => item.status === 'done' && !toUpload.includes(item))
      .map<UploadSuccess>(item => ({
        success: true,
        file: item.file,
        attachmentId: item.attachmentId!,
        secureUrl: item.secureUrl!,
      }))

    return [...doneAlready, ...results]
  }

  clearQueue(): void {
    this.uploadQueue.forEach(item => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
    })
    this.uploadQueue = []
  }

  async retry(id: string): Promise<UploadResult | null> {
    const item = this.uploadQueue.find(i => i.id === id)
    if (!item || item.status === 'done') return null

    item.status = 'uploading'
    item.error = undefined
    const res = await this.uploadSingleSafe(item)

    if (res.success) {
      item.status = 'done'
      item.attachmentId = res.attachmentId
      item.secureUrl = res.secureUrl
      item.error = undefined
    } else {
      item.status = 'error'
      item.error = res.error
    }

    return res
  }

  private queueFile(file: File): void {
    if (this.uploadQueue.length >= MAX_FILES) {
      this.onMaxFilesReached()
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      this.onValidationError(file, 'too_large')
      return
    }

    if (!this.isDroppableMime(file.type)) {
      this.onValidationError(file, 'unsupported_mime')
      return
    }

    const pending: PendingUpload = {
      id: this.generateId(),
      file,
      previewUrl: this.createPreviewUrl(file),
      status: 'pending',
    }

    this.uploadQueue.push(pending)
    this.onFileQueued(pending)
  }

  private async uploadSingleSafe(pending: PendingUpload): Promise<UploadResult> {
    try {
      pending.status = 'uploading'
      const data = await this.uploadFn(pending.file)
      return {
        success: true,
        file: pending.file,
        attachmentId: data.attachmentId,
        secureUrl: data.secureUrl,
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'upload_failed'
      return { success: false, file: pending.file, error: message }
    }
  }

  private handlePaste = (e: ClipboardEvent): void => {
    const items = e.clipboardData?.items
    if (!items) return

    const fileItems = Array.from(items).filter(item => item.kind === 'file')

    // No file on the clipboard — pure text paste, let the browser default run.
    if (fileItems.length === 0) return

    const pasteableItems = fileItems.filter(item => this.isPasteableMime(item.type))

    // A file WAS pasted but none are accepted image types. This is common on
    // macOS, where Cmd-C on a HEIC/TIFF in Finder yields a real file item with
    // that MIME. Stop the default (which would otherwise drop the filename /
    // nothing) and tell the user why, instead of failing silently.
    if (pasteableItems.length === 0) {
      e.preventDefault()
      const file = fileItems[0].getAsFile()
      if (file) this.onValidationError(file, 'unsupported_mime')
      return
    }

    const plainText = e.clipboardData?.getData('text/plain') ?? ''
    e.preventDefault()

    if (plainText) this.insertTextAtCursor(this.textArea, plainText)

    pasteableItems.forEach(item => {
      const file = item.getAsFile()
      if (file) this.queueFile(file)
    })
  }

  private handleDragEnter = (e: DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    if (this.dragContainsFiles(e)) {
      this.widgetContainer.classList.add('drop-zone-active')
    }
  }

  private handleDragOver = (e: DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  }

  private handleDragLeave = (e: DragEvent): void => {
    if (e.relatedTarget && !this.widgetContainer.contains(e.relatedTarget as Node)) {
      this.widgetContainer.classList.remove('drop-zone-active')
    }
  }

  private handleDrop = (e: DragEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    this.widgetContainer.classList.remove('drop-zone-active')

    const files = e.dataTransfer?.files
    if (!files || files.length === 0) return

    Array.from(files).forEach(file => {
      if (this.isDroppableMime(file.type)) {
        this.queueFile(file)
      } else {
        this.onValidationError(file, 'unsupported_mime')
      }
    })
  }

  private isPasteableMime(mime: string): boolean {
    return PASTE_MIME_WHITELIST.includes(mime)
  }

  private isDroppableMime(mime: string): boolean {
    return DROP_MIME_WHITELIST.includes(mime)
  }

  private createPreviewUrl(file: File): string | null {
    if (file.type.startsWith('image/')) return URL.createObjectURL(file)
    return null
  }

  private dragContainsFiles(e: DragEvent): boolean {
    return Array.from(e.dataTransfer?.types || []).includes('Files')
  }

  private insertTextAtCursor(textArea: HTMLTextAreaElement, text: string): void {
    const start = textArea.selectionStart ?? textArea.value.length
    const end = textArea.selectionEnd ?? textArea.value.length
    textArea.value = textArea.value.slice(0, start) + text + textArea.value.slice(end)
    const caret = start + text.length
    textArea.setSelectionRange(caret, caret)
    textArea.dispatchEvent(new Event('input', { bubbles: true }))
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 11)
  }
}
